/**
 * 🔐 ADMIN VERIFICATION REQUIREMENTS SERVICE
 * Manages verification tier rules and their configurations
 */

import apiClient from './apiClient';

// ============================================
// TypeScript Types & Interfaces
// ============================================

/**
 * Vehicle tier types that can be allowed per verification level
 */
export type VehicleTier =
  | 'economy'
  | 'standard'
  | 'comfort'
  | 'premium'
  | 'luxury'
  | 'executive'
  | 'sports'
  | 'commercial';

/**
 * Service types that can be allowed per verification level
 */
export type ServiceType = 'self-drive' | 'chauffeur-driven' | 'airport-transfer';

/**
 * Action types tracked in the audit log
 */
export type AuditAction = 'created' | 'updated' | 'deleted' | 'reordered';

/**
 * Main verification requirement entity
 */
export interface VerificationRequirement {
  id: string;
  rule_name: string;
  phone_verified: boolean;
  driver_license_verified: boolean;
  identity_verified: boolean;
  is_verified: boolean;
  max_booking_amount: number | null;
  allowed_vehicle_tiers: VehicleTier[] | null;
  allowed_service_types: ServiceType[] | null;
  min_security_deposit: number;
  security_deposit_rate: number;
  can_be_host: boolean;
  can_be_driver: boolean;
  priority: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

/**
 * DTO for creating a new verification requirement
 */
export interface CreateVerificationRequirementDTO {
  rule_name: string;
  phone_verified: boolean;
  driver_license_verified: boolean;
  identity_verified: boolean;
  is_verified: boolean;
  max_booking_amount?: number | null;
  allowed_vehicle_tiers?: VehicleTier[] | null;
  allowed_service_types?: ServiceType[] | null;
  min_security_deposit: number;
  security_deposit_rate?: number;
  can_be_host: boolean;
  can_be_driver: boolean;
  is_active?: boolean;
}

/**
 * DTO for updating an existing verification requirement
 * All fields are optional - only send what you want to change
 */
export interface UpdateVerificationRequirementDTO {
  rule_name?: string;
  phone_verified?: boolean;
  driver_license_verified?: boolean;
  identity_verified?: boolean;
  is_verified?: boolean;
  max_booking_amount?: number | null;
  allowed_vehicle_tiers?: VehicleTier[] | null;
  allowed_service_types?: ServiceType[] | null;
  min_security_deposit?: number;
  security_deposit_rate?: number;
  can_be_host?: boolean;
  can_be_driver?: boolean;
  is_active?: boolean;
}

/**
 * Item for reordering priorities
 */
export interface ReorderItem {
  id: string;
  priority: number;
}

/**
 * Audit log entry
 */
export interface VerificationRequirementAuditLog {
  id: string;
  requirement_id: string;
  changed_by: string;
  action: AuditAction;
  old_values: Record<string, any> | null;
  new_values: Record<string, any> | null;
  created_at: string;
  admin_email?: string;
  admin_first_name?: string;
  admin_last_name?: string;
  requirement_name?: string;
}

/**
 * Pagination metadata
 */
export interface PaginationMeta {
  page: number;
  limit: number;
  total_count: number;
  total_pages: number;
  has_next: boolean;
  has_previous: boolean;
}

/**
 * API Response wrapper for single requirement
 */
export interface VerificationRequirementResponse {
  success: boolean;
  data: VerificationRequirement;
  message?: string;
}

/**
 * API Response wrapper for list of requirements
 */
export interface VerificationRequirementsListResponse {
  success: boolean;
  data: VerificationRequirement[];
}

/**
 * API Response wrapper for audit log
 */
export interface AuditLogResponse {
  success: boolean;
  data: {
    logs: VerificationRequirementAuditLog[];
    pagination: PaginationMeta;
  };
}

/**
 * API Response wrapper for reorder operation
 */
export interface ReorderResponse {
  success: boolean;
  message: string;
  data: VerificationRequirement[];
}

// ============================================
// Service Methods - Real API Implementation
// ============================================

const realApiService = {
  /**
   * 📋 List all verification requirements
   * @param isActive - Optional filter for active/inactive rules
   * @returns List of verification requirements
   */
  async listVerificationRequirements(
    isActive?: boolean
  ): Promise<VerificationRequirementsListResponse> {
    const params = isActive !== undefined ? { is_active: isActive } : {};
    const response = await apiClient.get<VerificationRequirementsListResponse>(
      '/api/v1/admin/verification-requirements',
      { params }
    );
    return response.data;
  },

  /**
   * ➕ Create a new verification requirement
   * @param data - The verification requirement data
   * @returns The created verification requirement
   */
  async createVerificationRequirement(
    data: CreateVerificationRequirementDTO
  ): Promise<VerificationRequirementResponse> {
    const response = await apiClient.post<VerificationRequirementResponse>(
      '/api/v1/admin/verification-requirements',
      data
    );
    return response.data;
  },

  /**
   * ✏️ Update an existing verification requirement
   * @param id - The requirement ID
   * @param data - Fields to update
   * @returns The updated verification requirement
   */
  async updateVerificationRequirement(
    id: string,
    data: UpdateVerificationRequirementDTO
  ): Promise<VerificationRequirementResponse> {
    const response = await apiClient.patch<VerificationRequirementResponse>(
      `/api/v1/admin/verification-requirements/${id}`,
      data
    );
    return response.data;
  },

  /**
   * 🗑️ Hard delete a verification requirement (permanent)
   * @param id - The requirement ID
   * @returns The deletion confirmation
   */
  async deleteVerificationRequirement(
    id: string
  ): Promise<VerificationRequirementResponse> {
    const response = await apiClient.delete<VerificationRequirementResponse>(
      `/api/v1/admin/verification-requirements/${id}`
    );
    return response.data;
  },

  /**
   * ✅ Activate a verification requirement
   * @param requirementId - The requirement ID
   * @returns The activated verification requirement
   */
  async activateVerificationRequirement(
    requirementId: string
  ): Promise<VerificationRequirementResponse> {
    const response = await apiClient.post<VerificationRequirementResponse>(
      '/api/v1/admin/verification-requirements/activate',
      { requirementId }
    );
    return response.data;
  },

  /**
   * ❌ Deactivate a verification requirement
   * @param requirementId - The requirement ID
   * @returns The deactivated verification requirement
   */
  async deactivateVerificationRequirement(
    requirementId: string
  ): Promise<VerificationRequirementResponse> {
    const response = await apiClient.post<VerificationRequirementResponse>(
      '/api/v1/admin/verification-requirements/deactivate',
      { requirementId }
    );
    return response.data;
  },

  /**
   * 🔄 Reorder verification requirements (change priorities)
   * @param items - Array of {id, priority} objects
   * @returns The updated list of requirements in new priority order
   */
  async reorderVerificationRequirements(
    items: ReorderItem[]
  ): Promise<ReorderResponse> {
    const response = await apiClient.post<ReorderResponse>(
      '/api/v1/admin/verification-requirements/reorder',
      items
    );
    return response.data;
  },

  /**
   * 📜 Get audit log for verification requirements
   * @param page - Page number (default: 1)
   * @param limit - Items per page (default: 50)
   * @returns Paginated audit log entries
   */
  async getAuditLog(
    page: number = 1,
    limit: number = 50
  ): Promise<AuditLogResponse> {
    const response = await apiClient.get<AuditLogResponse>(
      '/api/v1/admin/verification-requirements/audit-log',
      { params: { page, limit } }
    );
    return response.data;
  },
};

// ============================================
// Service - Real API Implementation
// ============================================

export const adminVerificationRequirementsService = {
  async listVerificationRequirements(isActive?: boolean) {
    return realApiService.listVerificationRequirements(isActive);
  },

  async createVerificationRequirement(data: CreateVerificationRequirementDTO) {
    return realApiService.createVerificationRequirement(data);
  },

  async updateVerificationRequirement(id: string, data: UpdateVerificationRequirementDTO) {
    return realApiService.updateVerificationRequirement(id, data);
  },

  async deleteVerificationRequirement(id: string) {
    return realApiService.deleteVerificationRequirement(id);
  },

  async activateVerificationRequirement(requirementId: string) {
    return realApiService.activateVerificationRequirement(requirementId);
  },

  async deactivateVerificationRequirement(requirementId: string) {
    return realApiService.deactivateVerificationRequirement(requirementId);
  },

  async reorderVerificationRequirements(items: ReorderItem[]) {
    return realApiService.reorderVerificationRequirements(items);
  },

  async getAuditLog(page?: number, limit?: number) {
    return realApiService.getAuditLog(page, limit);
  },
};

// Default export
export default adminVerificationRequirementsService;
